import { BaseApiService } from '@/shared/api/services/base.service';
import { authLogger } from '@/shared/lib/logger';
import { ClassTypes } from '@/shared/constants';
import type { ProfileData } from '@/types/profile';
import type {
  AuthCredentials,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ValidateResetTokenRequest,
  ResetPasswordRequest,
} from '@/types/auth';

/**
 * Auth wire shape mirrors /api/v1/user (UserProfileResource) plus
 * `meta.token`. Same wire shape for every user-returning endpoint, so the
 * client uses one decoding path. The Sanctum token rides on document-level
 * `meta.token` — never on `data.meta` — and `meta.default_project_id` is
 * present on googleSignIn / verifyEmailCode responses for new-user flows.
 */
interface AuthResponse {
  data: {
    type: string;
    id: string;
    attributes: ProfileData;
  };
  meta?: {
    token?: string;
    default_project_id?: string;
  };
}

interface ParsedProfile {
  user: AuthCredentials['user'];
  profileData: ProfileData;
}

function parseProfileFromAuthResponse(response: AuthResponse): ParsedProfile {
  const profileUser = response.data.attributes.user;
  if (!profileUser) {
    throw new Error('No user data in auth response');
  }

  // Merge top-level JSON:API id onto the user (id is not in attributes per
  // JSON:API spec) — same merge profile.service.ts does on /user.
  const profileData: ProfileData = {
    ...response.data.attributes,
    user: { ...profileUser, id: response.data.id },
  };

  return {
    user: {
      id: response.data.id,
      name: profileUser.name,
      email: profileUser.email,
      locale: profileUser.locale,
      is_email_verified: profileUser.is_email_verified,
      has_completed_renders: profileUser.has_completed_renders,
      should_show_onboarding: profileUser.should_show_onboarding,
      email_verified_at: profileUser.email_verified_at ?? null,
      created_at: profileUser.created_at,
      updated_at: profileUser.updated_at ?? profileUser.created_at,
      tokens_balance: response.data.attributes.tokens?.current_balance,
    },
    profileData,
  };
}

function transformAuthResponse(response: AuthResponse): AuthCredentials {
  const token = response.meta?.token;
  if (!token) {
    throw new Error('No token in auth response');
  }

  const { user, profileData } = parseProfileFromAuthResponse(response);

  return {
    user,
    token,
    profileData,
    defaultProjectId: response.meta?.default_project_id,
  };
}

class AuthService extends BaseApiService {
  private getDeviceName(): string {
    if (typeof window === 'undefined') return 'Server';

    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;

    // Try to detect device type
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return `iOS Device (${platform})`;
    } else if (/Android/.test(userAgent)) {
      return 'Android Device';
    } else if (/Mac/.test(platform)) {
      return `Mac (${platform})`;
    } else if (/Win/.test(platform)) {
      return `Windows (${platform})`;
    } else if (/Linux/.test(platform)) {
      return `Linux (${platform})`;
    }

    return `Web Browser (${platform || 'Unknown'})`;
  }

  async login(credentials: LoginRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...credentials,
          device_name: credentials.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/login',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.login' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async register(credentials: RegisterRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...credentials,
          device_name: credentials.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/register',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.register' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async logout(): Promise<void> {
    // Best-effort server-side revoke. AuthTokenService.clearAuth() runs in
    // auth.store regardless — local state is cleared even if the API call
    // fails (offline, 401 on already-expired token, etc.).
    try {
      await this.post('/v1/auth/logout', undefined, undefined, {
        error: { silent: true },
      });
    } catch (error) {
      authLogger.debug('Logout API call failed (continuing local cleanup):', error);
    }
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    await this.post('/v1/auth/password/forgot', payload, undefined, {
      loading: { trackEndpoint: 'auth.forgot-password' },
    });
  }

  async validateResetToken(data: ValidateResetTokenRequest): Promise<void> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    await this.post('/v1/auth/password/validate-token', payload, undefined, {
      loading: { trackEndpoint: 'auth.validate-token' },
    });
  }

  async resetPassword(data: ResetPasswordRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...data,
          device_name: data.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/password/reset',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.reset-password' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  /**
   * Exchange a Google OAuth authorization code (web flow) for an API token.
   * `redirect_uri` must match the one used to initiate the OAuth round-trip
   * (server validates it against the allowlist).
   */
  async googleCodeExchange(data: {
    code: string;
    redirect_uri: string;
  }): Promise<AuthCredentials> {
    const response = await this.post<AuthResponse>(
      '/v1/auth/google/code-exchange',
      data as unknown as Record<string, unknown>,
      undefined,
      {
        loading: { trackEndpoint: 'auth.google-code-exchange' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async sendVerificationCode(): Promise<void> {
    await this.post('/v1/auth/verification-code/send');
  }

  async verifyEmailCode(
    code: string
  ): Promise<{
    user: AuthCredentials['user'];
    profileData: AuthCredentials['profileData'];
    defaultProjectId: string | null;
  }> {
    const payload = {
      data: {
        type: 'verification',
        attributes: { code },
      },
    };

    // verifyCode now returns the same UserProfileResource shape as /user —
    // the auth flow's last flat-shape endpoint is gone. Same parser as
    // login/register; only difference is the response carries no fresh
    // token (the caller is already authenticated via the existing session).
    const response = await this.post<AuthResponse>(
      '/v1/auth/verification-code/verify',
      payload,
      undefined,
      { error: { silent: false } }
    );

    const { user, profileData } = parseProfileFromAuthResponse(response);
    return {
      user,
      profileData,
      defaultProjectId: response.meta?.default_project_id ?? null,
    };
  }

  // Redirect to Google OAuth - returns the redirect URL
  async getGoogleOAuthUrl(): Promise<string> {
    const response = await this.httpGet<{
      data: { attributes: { redirect_url: string } };
    }>(
      '/v1/auth/social/google?app=web',
      undefined,
      {
        loading: { trackEndpoint: 'auth.google-oauth-url' },
      }
    );
    return response.data.attributes.redirect_url;
  }

  async validateActivationToken(data: {
    email: string;
    activation_token: string;
  }): Promise<{
    is_valid: boolean;
    already_activated: boolean;
    email: string;
    error?: string;
  }> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    const response = await this.post<{
      data: {
        attributes: {
          is_valid: boolean;
          already_activated: boolean;
          email: string;
          error?: string;
        };
      };
    }>('/v1/auth/activate/validate-token', payload, undefined, {
      loading: { trackEndpoint: 'auth.validate-activation' },
      error: { silent: false },
    });

    return response.data.attributes;
  }

  async activateAccount(data: {
    email: string;
    activation_token: string;
    password: string;
  }): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...data,
          device_name: this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/activate',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.activate' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }
}

export const authService = new AuthService();
export default authService;
